// src/pages/admin/MetricsDashboard.js
import React, { useMemo, useState, useEffect } from 'react';
import { Grid, Paper, Typography, Box, Divider, LinearProgress } from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const RANK_COLORS = [
    '#FF5722', // 1º - vermelho-laranja
    '#FF9800', // 2º - laranja
    '#FFC107', // 3º - âmbar
    '#8BC34A', // 4º - verde-claro
    '#4CAF50', // 5º - verde
    '#009688', // 6º - teal
    '#2196F3', // 7º - azul
    '#3F51B5', // 8º - índigo
    '#9C27B0', // 9º - roxo
    '#E91E63', // 10º - rosa
    '#795548', // 11º - marrom
    '#607D8B', // 12º - cinza-azulado
];

const MEDAL_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32']; // ouro, prata, bronze

function MetricCard({ title, value }) {
    return (
        <Paper elevation={3} sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', textAlign: 'center' }}>
            <Typography component="h2" variant="h6" color="text.secondary" gutterBottom>{title}</Typography>
            <Typography component="p" variant="h4" color="primary">{value}</Typography>
        </Paper>
    );
}

function ZonaRankingCard({ zonaData, totalParceiros }) {
    if (!zonaData || zonaData.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">Nenhum dado de zona disponível.</Typography>
            </Box>
        );
    }

    const maxCount = zonaData[0]?.count || 1;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, overflowY: 'auto', maxHeight: 330, pr: 1 }}>
            {zonaData.map(({ zona, count }, index) => {
                const pct = Math.round((count / totalParceiros) * 100);
                const barPct = Math.round((count / maxCount) * 100);
                const color = RANK_COLORS[index] || '#90A4AE';
                const isMedal = index < 3;

                return (
                    <Box key={zona} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Badge de posição */}
                        <Box sx={{
                            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: isMedal ? MEDAL_COLORS[index] : '#e0e0e0',
                            boxShadow: isMedal ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                        }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: isMedal ? '#fff' : '#757575' }}>
                                {index + 1}
                            </Typography>
                        </Box>

                        {/* Nome da zona + barra */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem', color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {zona}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.82rem', color, ml: 1, flexShrink: 0 }}>
                                    {count} <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>({pct}%)</Typography>
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={barPct}
                                sx={{
                                    height: 8, borderRadius: 4,
                                    bgcolor: '#f0f0f0',
                                    '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 }
                                }}
                            />
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

function MetricsDashboard({ usuarios: initialUsers }) {
    const [usuarios, setUsuarios] = useState(initialUsers || []);

    useEffect(() => {
        if (!initialUsers) {
            const fetchAllUsers = async () => {
                const snapshot = await getDocs(collection(db, "usuarios"));
                setUsuarios(snapshot.docs.map(d => ({ ...d.data(), id: d.id })));
            };
            fetchAllUsers();
        } else {
            setUsuarios(initialUsers);
        }
    }, [initialUsers]);

    const metrics = useMemo(() => {
        const partners = usuarios.filter(u => u.tipoUsuario !== 'administrador');
        const porZonaMap = partners.reduce((acc, user) => {
            if (user.zonaAtuacao) {
                user.zonaAtuacao.forEach(zona => { acc[zona] = (acc[zona] || 0) + 1; });
            }
            return acc;
        }, {});

        const porZonaRanked = Object.entries(porZonaMap)
            .map(([zona, count]) => ({ zona, count }))
            .sort((a, b) => b.count - a.count);

        return {
            totalParceiros: partners.length,
            ativos: partners.filter(u => u.status === 'ativo').length,
            inativos: partners.filter(u => u.status === 'inativo').length,
            autonomos: partners.filter(u => u.tipoUsuario === 'autonomo').length,
            imobiliarias: partners.filter(u => u.tipoUsuario === 'imobiliaria').length,
            porZonaRanked,
        };
    }, [usuarios]);

    const statusChartData = {
        labels: ['Ativos', 'Inativos'],
        datasets: [{ data: [metrics.ativos, metrics.inativos], backgroundColor: ['#4caf50', '#f44336'], borderWidth: 1 }],
    };

    return (
        <Box>
            <Grid container spacing={3}>
                {/* LINHA 1: KPIs */}
                <Grid item xs={6} sm={4} md={2.4}><MetricCard title="Total" value={metrics.totalParceiros} /></Grid>
                <Grid item xs={6} sm={4} md={2.4}><MetricCard title="Ativos" value={metrics.ativos} /></Grid>
                <Grid item xs={6} sm={4} md={2.4}><MetricCard title="Inativos" value={metrics.inativos} /></Grid>
                <Grid item xs={6} sm={6} md={2.4}><MetricCard title="Autônomos" value={metrics.autonomos} /></Grid>
                <Grid item xs={6} sm={6} md={2.4}><MetricCard title="Imobiliárias" value={metrics.imobiliarias} /></Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Grid container spacing={3}>
                {/* LINHA 2: Zona ranking + Doughnut */}
                <Grid item xs={12} md={8}>
                    <Paper elevation={3} sx={{ p: 3, height: 400, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">Parceiros por Zona de Atuação</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {metrics.porZonaRanked.length} zona{metrics.porZonaRanked.length !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                        <ZonaRankingCard zonaData={metrics.porZonaRanked} totalParceiros={metrics.totalParceiros || 1} />
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper elevation={3} sx={{ p: 2, height: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography variant="h6" align="center" gutterBottom>Status dos Parceiros</Typography>
                        <Box sx={{ maxWidth: '280px', margin: 'auto' }}>
                            <Doughnut data={statusChartData} />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default MetricsDashboard;
